import type { ILopDetail } from "@/types/(company)/payrollRuns.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";

// ── Enums ─────────────────────────────────────────────────────────────────────
export type DisputeStatus =
  "PENDING" | "UNDER_REVIEW" | "RESOLVED" | "REJECTED";

export type DisputeCategory =
  | "WRONG_AMOUNT"
  | "MISSING_EARNING"
  | "WRONG_DEDUCTION"
  | "MISSING_OVERTIME"
  | "WRONG_LEAVE_DEDUCTION"
  | "WRONG_STATUTORY"
  | "OTHER";

export type DisputeLineType = "EARNING" | "BENEFIT" | "DEDUCTION";
export type DisputeLineDecision = "PENDING" | "ACCEPTED" | "REJECTED";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface IDisputeLine {
  id: number;
  lineId: number | null;
  lineType: DisputeLineType;
  name: string;
  /** The days behind a disputed Loss of Pay (B45). */
  lopDetail?: ILopDetail | null;
  /** What HR agreed the line should be; null until accepted (B44). */
  approvedAmount?: number | null;
  /** Loss of Pay dates the employee disputes. */
  disputedDates?: string[];
  /** What this line pays once settled. */
  adjustment?: number;
  currentAmount: number;
  proposedAmount: number;
  diff: number;
  note: string | null;
  hrDecision: DisputeLineDecision;
  hrNote: string | null;
}

export interface IDisputeComment {
  id: number;
  userId: number;
  userName: string;
  profileImage: string | null;
  message: string;
  createdAt: string;
}

export interface IDispute {
  id: number;
  payrollRunId: number;
  payrollRunItemId: number;
  raisedBy: number;
  raisedByUser: {
    id: number;
    fullName: string;
    profileImage: string | null;
  } | null;
  payrollRun: {
    id: number;
    runCode: string;
    periodStart: string;
    periodEnd: string;
    status: string;
  } | null;
  category: DisputeCategory;
  status: DisputeStatus;
  description: string;
  hrResponse: string | null;
  resolvedBy: number | null;
  resolvedByUser: {
    id: number;
    fullName: string;
    profileImage: string | null;
  } | null;
  resolvedAt: string | null;
  wpsSettledAt: string | null;
  wpsPaymentFileId: number | null;
  /** How it was settled (B47); null on disputes settled before. */
  settledByUser?: { id: number; fullName: string } | null;
  settlementMethod?:
    | "WPS"
    | "CASH"
    | "BANK_TRANSFER"
    | "CHEQUE"
    | "RECOVERED"
    | "NO_PAYMENT_DUE"
    | null;
  settlementAmount?: number | null;
  settlementReference?: string | null;
  settlementNote?: string | null;
  lines: IDisputeLine[];
  comments: IDisputeComment[];
  totalProposedDiff: number;
  createdAt: string;
  updatedAt: string;
}

export interface ISettleableDispute extends IDispute {
  adjustmentAmount: number;
  paymentMethod: string | null;
  currencyCode: string | null;
  eligibleForWps: boolean;
}

export interface ISettleResponse {
  settledCount: number;
  settledIds: number[];
  skipped: Array<{ disputeId: number; reason: string }>;
  paymentFiles: Array<{
    id: number;
    fileName: string;
    employeeCount: number;
    totalAmount: number;
    currency: string;
  }>;
}

// ── Request payloads ──────────────────────────────────────────────────────────
export interface ICreateDisputeLine {
  lineId?: number | null;
  lineType: DisputeLineType;
  name: string;
  // No currentAmount: the server reads it from the payslip (S8, G7).
  proposedAmount: number;
  note?: string;
  /** Loss of Pay only; the server works the amount out from them (B44). */
  disputedDates?: string[];
}

export interface ICreateDisputePayload {
  payslipId: number;
  category: DisputeCategory;
  description: string;
  proposedLines: ICreateDisputeLine[];
}

export interface IRespondDisputePayload {
  id: number;
  status: DisputeStatus;
  hrResponse: string;
  lineDecisions?: Array<{
    disputeLineId: number;
    decision: DisputeLineDecision;
    hrNote?: string;
    /** The agreed amount on an accepted line (B44). */
    approvedAmount?: number;
  }>;
}

export interface IListDisputesParams {
  page?: number;
  limit?: number;
  status?: DisputeStatus;
  search?: string;
  payrollRunId?: number;
  payslipItemId?: number;
}

export interface IDisputeStats {
  total: number;
  pending: number;
  underReview: number;
  resolved: number;
  rejected: number;
}

// ── API ───────────────────────────────────────────────────────────────────────
export const payrollDisputesApi = createApi({
  reducerPath: "payrollDisputesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "msDisputes",
    "msDispute",
    "msDisputeStats",
    "companyDisputes",
    "companyDispute",
  ],
  endpoints: (builder) => ({
    // ── Employee side ──────────────────────────────────
    createDispute: builder.mutation<
      IApiResponse<IDispute>,
      ICreateDisputePayload
    >({
      query: ({ payslipId, ...body }) => ({
        url: `/employee/payroll/payslips/${payslipId}/disputes`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["msDisputes", "msDispute", "msDisputeStats"],
    }),
    listMyDisputes: builder.query<
      IApiPaginatedResponse<IDispute[]>,
      IListDisputesParams | void
    >({
      query: (params) => ({
        url: "/employee/payroll/disputes",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.payslipItemId
            ? { payslipItemId: params.payslipItemId }
            : {}),
        },
      }),
      providesTags: ["msDisputes"],
    }),
    getMyDisputeStats: builder.query<IApiResponse<IDisputeStats>, void>({
      query: () => ({
        url: "/employee/payroll/disputes-stats",
        method: "GET",
      }),
      providesTags: ["msDisputeStats"],
    }),
    getMyDispute: builder.query<IApiResponse<IDispute>, number>({
      query: (id) => ({
        url: `/employee/payroll/disputes/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "msDispute", id }],
    }),
    addMyDisputeComment: builder.mutation<
      IApiResponse<IDispute>,
      { id: number; message: string }
    >({
      query: ({ id, message }) => ({
        url: `/employee/payroll/disputes/${id}/comments`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "msDispute", id }],
    }),

    // ── Company / HR side ──────────────────────────────
    listCompanyDisputes: builder.query<
      IApiPaginatedResponse<IDispute[]>,
      IListDisputesParams | void
    >({
      query: (params) => ({
        url: "/company/payroll/disputes",
        method: "GET",
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.search ? { search: params.search } : {}),
        },
      }),
      providesTags: ["companyDisputes"],
    }),
    getCompanyDispute: builder.query<IApiResponse<IDispute>, number>({
      query: (id) => ({
        url: `/company/payroll/disputes/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "companyDispute", id }],
    }),
    respondDispute: builder.mutation<
      IApiResponse<IDispute>,
      IRespondDisputePayload
    >({
      query: ({ id, ...body }) => ({
        url: `/company/payroll/disputes/${id}/respond`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "companyDisputes",
        { type: "companyDispute", id },
      ],
    }),
    addCompanyDisputeComment: builder.mutation<
      IApiResponse<IDispute>,
      { id: number; message: string }
    >({
      query: ({ id, message }) => ({
        url: `/company/payroll/disputes/${id}/comments`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "companyDispute", id }],
    }),
    listSettleableDisputes: builder.query<
      IApiResponse<ISettleableDispute[]>,
      void
    >({
      query: () => ({
        url: "/company/payroll/disputes/settleable",
        method: "GET",
      }),
      providesTags: ["companyDisputes"],
    }),
    settleDisputesWps: builder.mutation<
      IApiResponse<ISettleResponse>,
      { disputeIds: number[] }
    >({
      query: (body) => ({
        url: "/company/payroll/disputes/settle-wps",
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyDisputes"],
    }),
    markDisputesSettled: builder.mutation<
      IApiResponse<{ settledCount: number; settledIds: number[] }>,
      {
        disputeIds: number[];
        /** How it was settled (B47); the amount is each dispute's own. */
        method:
          "CASH" | "BANK_TRANSFER" | "CHEQUE" | "RECOVERED" | "NO_PAYMENT_DUE";
        reference?: string;
        note?: string;
      }
    >({
      query: (body) => ({
        url: "/company/payroll/disputes/mark-settled",
        method: "POST",
        body,
      }),
      invalidatesTags: ["companyDisputes"],
    }),
  }),
});

export const {
  useCreateDisputeMutation,
  useListMyDisputesQuery,
  useGetMyDisputeStatsQuery,
  useGetMyDisputeQuery,
  useAddMyDisputeCommentMutation,
  useListCompanyDisputesQuery,
  useGetCompanyDisputeQuery,
  useRespondDisputeMutation,
  useAddCompanyDisputeCommentMutation,
  useListSettleableDisputesQuery,
  useSettleDisputesWpsMutation,
  useMarkDisputesSettledMutation,
} = payrollDisputesApi;
